import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Zap, 
  FileText, 
  Download,
  Eye,
  Clock,
  BarChart3,
  ChevronDown
} from 'lucide-react';

const LessonSidebar = ({ lesson, userRole, analytics }) => {
  if (!lesson) return null;

  return (
    <div className="space-y-6">
      {/* Analytics Card - Show video statistics (Teachers & Admin only) */}
      {(userRole === 'teacher' || userRole === 'admin') && analytics?.data?.stats && (
        <AnalyticsCard stats={analytics.data.stats} />
      )}

      {/* Tips Card */}
      <TipsCard />

      {/* Files Card */}
      <FilesCard filePath={lesson.file_path} />
    </div>
  );
};

const AnalyticsCard = ({ stats }) => {
  const formatTime = (seconds) => {
    if (!seconds) return '0د';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}س ${minutes}د`;
    return `${minutes}د`;
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-orange-200 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-orange-600/20 p-3 rounded-xl">
          <BarChart3 size={24} className="text-orange-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">إحصائيات الفيديو</h3>
          <p className="text-gray-600 text-xs">بيانات دقيقة من Bunny Stream</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Views */}
        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-orange-100">
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-blue-600" />
            <span className="text-sm font-medium text-gray-700">المشاهدات</span>
          </div>
          <span className="font-bold text-gray-900">{stats.views || 0}</span>
        </div>

        {/* Average Watch Time */}
        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-orange-100">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-green-600" />
            <span className="text-sm font-medium text-gray-700">وقت المشاهدة</span>
          </div>
          <span className="font-bold text-gray-900">{formatTime(stats.averageWatchTime)}</span>
        </div>

        {/* Completion Rate */}
        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-orange-100">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-purple-600" />
            <span className="text-sm font-medium text-gray-700">معدل الإكمال</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                style={{ width: `${stats.completionRate || 0}%` }}
              />
            </div>
            <span className="font-bold text-gray-900 text-sm">{stats.completionRate || 0}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TipsCard = () => {
  const [isExpanded, setIsExpanded] = useState(true);

  const tips = [
    {
      icon: '📝',
      text: 'دوّن ملاحظاتك أثناء مشاهدة الفيديو',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: '⏸️',
      text: 'خذ استراحة قصيرة بعد كل 30 دقيقة',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: '❓',
      text: 'لا تتردد في طرح أي سؤال عند الحاجة',
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: '🔄',
      text: 'كرر المشاهدات الصعبة لفهمها جيداً',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-lg overflow-hidden border border-slate-700/50">
      {/* Header - Collapsible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-slate-800/50 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-3 rounded-xl shadow-lg">
            <Zap size={24} className="text-white" fill="white" />
          </div>
          <div className="text-left">
            <h3 className="font-black text-white text-lg">نصائح تعليمية</h3>
            <p className="text-xs text-slate-400">لتحسين تجربتك التعليمية</p>
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-3 border-t border-slate-700/50">
          {tips.map((tip, index) => (
            <div
              key={index}
              className="group relative flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-700/30 to-slate-600/20 border border-slate-600/30 hover:border-slate-500/50 hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-slate-600/40 transition-all duration-300"
            >
              {/* Gradient Background Accent */}
              <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${tip.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
              
              {/* Icon */}
              <div className="flex-shrink-0 text-2xl mt-0.5 relative z-10">
                {tip.icon}
              </div>
              
              {/* Text */}
              <p className="text-sm text-slate-200 font-medium leading-relaxed relative z-10">
                {tip.text}
              </p>
            </div>
          ))}

          {/* Footer Message */}
          <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg flex items-start gap-2">
            <span className="text-lg mt-0.5">💡</span>
            <p className="text-xs text-blue-200">تذكر: النجاح يأتي من الاستمرار والممارسة المنتظمة</p>
          </div>
        </div>
      )}
    </div>
  );
};

const FilesCard = ({ filePath }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-bold text-gray-900 flex items-center gap-2">
        <FileText size={20} className="text-primary-600" />
        ملفات الدرس
      </h3>
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
        {filePath ? '1 ملف' : 'لا يوجد'}
      </span>
    </div>

    {filePath ? (
      <div className="space-y-3">
        <a
          href={filePath}
          target="_blank"
          rel="noreferrer noopener"
          className="flex items-center gap-3 p-3 rounded-xl border border-gray-50 hover:bg-gray-50 transition-all group hover:border-primary-100"
        >
          <div className="w-12 h-12 rounded-lg bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-all shadow-sm">
            <FileText size={24} />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-bold text-gray-900 truncate">ملف مرفقات الدرس</p>
            <p className="text-xs text-gray-400">PDF / ملفات إضافية</p>
          </div>
          <Download size={18} className="text-gray-300 group-hover:text-primary-600 transition-colors" />
        </a>

        <div className="text-xs text-gray-500 mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="flex items-center gap-1 mb-1">
            <span className="text-primary-600" aria-hidden="true">💡</span>
            يمكنك تحميل الملفات للرجوع إليها لاحقاً
          </p>
        </div>
      </div>
    ) : (
      <div className="text-center py-8">
        <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="text-gray-300" size={24} />
        </div>
        <p className="text-sm text-gray-400 italic">لا توجد ملفات مرفقة لهذا الدرس</p>
      </div>
    )}
  </div>
);

LessonSidebar.propTypes = {
  lesson: PropTypes.shape({
    progress: PropTypes.object,
    file_path: PropTypes.string,
  }),
  userRole: PropTypes.string,
  analytics: PropTypes.object,
};

AnalyticsCard.propTypes = {
  stats: PropTypes.shape({
    views: PropTypes.number,
    totalWatchTime: PropTypes.number,
    averageWatchTime: PropTypes.number,
    duration: PropTypes.number,
    completionRate: PropTypes.number,
  }),
};

FilesCard.propTypes = {
  filePath: PropTypes.string,
};

export default LessonSidebar;
